import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useT } from "../lib/LanguageContext";
import { PILLAR_TO_CATEGORY, PILLAR_SLUGS as SLUGS } from "../lib/serviceCategories";
import { RichText } from "../components/RichText";

const IMAGES = {
  "creation-de-contenu":
    "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/hvdqb4wv_7.1.png",
  "social-media-management":
    "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=1400&q=85",
  "branding-identite-visuelle":
    "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/241v9gzn_8.5.jpeg",
  "creations-print":
    "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/qm3axu07_16.jpeg",
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const data = t(`serviceDetail.data.${slug}`);
  const image = IMAGES[slug];

  useEffect(() => {
    if (!image) navigate("/vision", { replace: true });
  }, [image, navigate]);

  if (!data || !image) return null;

  return (
    <main data-testid={`service-detail-${slug}`} className="bg-white">
      <section className="pt-36 pb-12 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/vision"
            data-testid="service-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#6B5CE7] hover:text-[#4D3FC8] transition-colors mb-8"
          >
            <ArrowLeft size={14} />
            {t("serviceDetail.back")}
          </Link>
          <Reveal>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-[#1A1528] leading-tight max-w-4xl">
              {data.title}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6">
        <div
          data-testid={`service-layout-${slug}`}
          className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
        >
          <Reveal className="lg:col-span-4">
            <img
              src={image}
              alt={data.label}
              className="w-full max-w-sm sm:max-w-md mx-auto lg:max-w-none aspect-square object-cover rounded-3xl shadow-xl"
            />
          </Reveal>

          <div className="lg:col-span-8 space-y-8 lg:space-y-10">
            <Reveal delay={0.1}>
              <p className="text-[#4A4558] font-light text-base md:text-lg leading-relaxed">
                <RichText text={data.description} />
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <ul className="space-y-3.5">
                {data.prestations.map((p, i) => (
                  <li
                    key={p}
                    data-testid={`presta-${slug}-${i}`}
                    className="flex items-baseline gap-5 text-[#1A1528] text-base md:text-lg font-light border-b border-[#F5E6A3]/60 pb-3"
                  >
                    <span className="font-serif text-[#6B5CE7] text-base font-medium">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-semibold">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.3}>
          <div
            data-testid={`service-actions-${slug}`}
            className="max-w-6xl mx-auto mt-14 md:mt-20 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/#reserver" data-testid={`service-cta-devis-${slug}`} className="btn-outline-violet w-full sm:w-auto justify-center">
              {t("common.devis")}
            </Link>
            <Link
              to={`/offres/${PILLAR_TO_CATEGORY[slug]}`}
              data-testid={`service-cta-offres-${slug}`}
              className="btn-outline-violet w-full sm:w-auto justify-center"
            >
              {t("common.decouvrirOffres")}
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="py-20 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SLUGS.filter((k) => k !== slug).map((key) => (
              <Link
                key={key}
                to={`/service/${key}`}
                data-testid={`other-service-${key}`}
                className="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-[#F5E6A3]/60"
              >
                <h3 className="font-serif text-xl text-[#1A1528] font-semibold">
                  {t(`serviceDetail.data.${key}.label`)}
                </h3>
                <p className="text-sm text-[#4A4558] mt-3 font-light">
                  {t("common.enSavoirPlus")} →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;
